import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import {
  LayoutDashboard,
  UserPlus,
  Users,
  MessageSquare,
  Megaphone,
  LogOut,
  ArrowLeft,
  Shield,
  Menu,
  X,
  CreditCard,
  BarChart3,
  Settings,
  LineChart,
  GraduationCap,
} from "lucide-react";
import { Logo, BRAND_NAME } from "./ui/Logo";

const adminNavItems = [
  { path: "/admin", icon: LayoutDashboard, label: "Panel" },
  { path: "/admin/users", icon: Users, label: "Usuarios" },
  { path: "/admin/aula", icon: GraduationCap, label: "Aurora Aula" },
  { path: "/admin/billing", icon: CreditCard, label: "Facturación" },
  { path: "/admin/contacts", icon: UserPlus, label: "Contactos" },
  { path: "/admin/feedback", icon: MessageSquare, label: "Feedback" },
  { path: "/admin/advertising", icon: Megaphone, label: "Publicidad" },
  { path: "/admin/uso", icon: LineChart, label: "Uso del sistema" },
  { path: "/admin/analytics", icon: BarChart3, label: "Analytics" },
  { path: "/admin/settings", icon: Settings, label: "Configuración" },
];

export const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 z-40 flex h-16 items-center justify-between border-b border-slate-700/90 bg-slate-800/95 px-3 backdrop-blur-sm lg:hidden">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="h-10 w-10 shrink-0 text-white hover:bg-slate-700"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
        <div className="flex min-w-0 flex-1 items-center justify-center gap-2 px-1 sm:gap-2.5">
          <Logo variant="wordmark" format="svg" className="h-7 w-auto shrink-0" alt="" />
          <span className="hidden min-w-0 truncate text-[13px] font-semibold tracking-tight text-white/95 sm:inline">
            {BRAND_NAME}
          </span>
          <span className="flex shrink-0 items-center gap-1.5 text-slate-400">
            <Shield className="h-4 w-4 text-sky-400" aria-hidden />
            <span className="truncate text-xs font-medium text-slate-300">Admin</span>
          </span>
        </div>
        <div className="h-10 w-10 shrink-0" aria-hidden />
      </header>

      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - dark theme for admin */}
      <aside
        className={`fixed top-0 left-0 h-full bg-slate-800 border-r border-slate-700 z-40 transition-transform duration-300 ease-in-out
          lg:translate-x-0 lg:w-64 w-64
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        <div className="flex h-16 items-center justify-center border-b border-slate-700 px-4 py-2">
          <Logo variant="icon" size="sm" className="h-9 w-9 opacity-[0.97]" alt={BRAND_NAME} />
        </div>

        <ScrollArea className="h-[calc(100vh-10rem)]">
          <nav className="p-4 space-y-2">
            {adminNavItems.map((item) => {
              const isActive = location.pathname === item.path || (item.path === "/admin" && location.pathname === "/admin");
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200
                    ${isActive
                      ? "bg-sky-600 text-white"
                      : "text-slate-300 hover:bg-slate-700 hover:text-white"}`}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700 bg-slate-800">
          <Link to="/dashboard" className="block mb-3">
            <Button variant="ghost" className="w-full justify-start text-slate-300 hover:bg-slate-700 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Ir a la app
            </Button>
          </Link>
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="h-10 w-10 rounded-full bg-slate-600 flex items-center justify-center">
              <Shield className="h-5 w-5 text-slate-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.nombre || user?.email}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:text-red-400 hover:bg-red-900/30"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen flex flex-col">
        <div className="p-4 lg:p-8 flex-1">
          {children}
        </div>
        <footer className="lg:ml-0 py-3 px-8 text-center text-slate-400 text-xs border-t border-slate-200 bg-white">
          <p className="text-slate-500">{BRAND_NAME} · Panel de Administración Interno</p>
        </footer>
      </main>
    </div>
  );
};

export default AdminLayout;
